import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiMail, FiArrowRight } = FiIcons;

const AboutPage = () => {
  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold leading-tight"
            >
              About <span className="gradient-text">Quantum Leverage</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              We build AI-powered tools that scale personal growth and organizational performance, transforming the way individuals and companies evolve.
            </motion.p>
          </motion.div>
          {/* Background Elements */}
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-orange/3 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square bg-gray-900 tech-border overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&h=800&fit=crop"
                  alt="Quantum Leverage Office"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -top-6 -right-6 w-24 h-24 flex items-center justify-center"
              >
                <img 
                  src="https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/icononly_transparent_nobuffer.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9pY29ub25seV90cmFuc3BhcmVudF9ub2J1ZmZlci5wbmciLCJpYXQiOjE3NTQ5MTY2MDUsImV4cCI6MzMyOTA5MTY2MDV9.92E3oR6MA9SmcZKdeU0lsdO-y2OGtrsHF2YwxSCzQDQ" 
                  alt="Quantum Leverage Logo"
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold">
                Our <span className="gradient-text">Mission</span>
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                At Quantum Leverage, we believe that the right technology can unlock human potential at unprecedented scales. Our mission is to create AI-powered platforms that don't just optimize processes—they transform how individuals and organizations evolve.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Founded in 2023, we're a team of engineers, designers, and business strategists passionate about building transformational tech for exponential people.
              </p>

              {/* Quick Links */}
              <div className="flex flex-col sm:flex-row gap-6 pt-4">
                <Link to="/about/team">
                  <motion.button
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-transparent border border-primary-orange text-primary-orange px-8 py-4 font-semibold hover:bg-primary-orange hover:text-black transition-all duration-300 flex items-center space-x-2 group"
                  >
                    <SafeIcon icon={FiUsers} className="w-5 h-5" />
                    <span>Meet Our Team</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
                <Link to="/about/contact">
                  <motion.button
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-transparent border border-gray-600 text-white px-8 py-4 font-semibold hover:border-primary-orange hover:text-primary-orange transition-all duration-300 flex items-center space-x-2 group"
                  >
                    <SafeIcon icon={FiMail} className="w-5 h-5" />
                    <span>Contact Us</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center space-y-16"
          >
            <div className="space-y-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-4xl font-bold"
              >
                Our <span className="gradient-text">Values</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-xl text-gray-300 max-w-3xl mx-auto"
              >
                The core principles that guide our work
              </motion.p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Innovation',
                  description: 'We constantly push the boundaries of what is possible with AI and human potential.',
                },
                {
                  title: 'Integrity',
                  description: 'We build trust through transparency, security, and ethical AI development.',
                },
                {
                  title: 'Impact',
                  description: 'We measure success by the transformational outcomes we create for our users.',
                },
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  viewport={{ once: true }}
                  className="glass-effect p-8 space-y-4 group hover:bg-gray-900/50 transition-all duration-300"
                >
                  <div className="h-1 w-16 bg-primary-orange"></div>
                  <h3 className="text-2xl font-bold group-hover:text-primary-orange transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;